import { Icon } from "./Icon";

export interface StepProgressProps {
  /** Names the navigation landmark, e.g. "Booking progress". */
  readonly label: string;
  /** Short step names, in order. Each must be unique. */
  readonly steps: readonly string[];
  /** The current step, from 0. */
  readonly current: number;
  /** When given, each finished step is a button that goes back to it. */
  readonly onSelect?: (index: number) => void;
  readonly className?: string;
}

/**
 * The patient app's one step indicator: numbered circles on a line, a check in each finished
 * step, the current step filled and later steps quiet, with the step's name under each circle.
 * Screen readers hear "Step n of m" and the current step through aria-current="step"; below
 * 400px only the current step's name stays on screen (styles.css, `.pa-steps`).
 */
export function StepProgress({ label, steps, current, onSelect, className }: StepProgressProps) {
  return (
    <nav
      className={className === undefined ? "pa-steps" : `pa-steps ${className}`}
      aria-label={label}
    >
      <p className="pa-steps-count sr-only">
        Step {current + 1} of {steps.length}
      </p>
      <ol className="pa-steps-list">
        {steps.map((name, index) => {
          const state = index < current ? "done" : index === current ? "current" : "upcoming";
          const content = (
            <>
              <span className="pa-step-mark" aria-hidden="true">
                {state === "done" ? <Icon name="check" /> : index + 1}
              </span>
              <span className="pa-step-label">{name}</span>
              {state === "done" ? <span className="sr-only">, done</span> : null}
            </>
          );
          return (
            <li
              key={name}
              className={`pa-step is-${state}`}
              aria-current={state === "current" ? "step" : undefined}
            >
              {state === "done" && onSelect !== undefined ? (
                <button type="button" className="pa-step-target" onClick={() => onSelect(index)}>
                  {content}
                </button>
              ) : (
                <span className="pa-step-target">{content}</span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
